import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutList, BookOpen, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "../lib/auth";
import NoIndex from "./NoIndex";
import { BrandLogo } from "./BrandLogo";
import useAccessibleOverlay from "../hooks/useAccessibleOverlay";

const NAV = [
  { to: "/support-workspace/cases", label: "Cases", icon: LayoutList },
  { to: "/support-workspace/knowledge", label: "Knowledge base", icon: BookOpen },
];

export default function SupportWorkspaceLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));
  return (
    <div className="min-h-screen bg-slate-50" data-testid="support-workspace-layout">
      <NoIndex title="Support Workspace · ConfirmPH" />
      <header className="sticky top-0 z-40 bg-[#0F1F18] text-white shadow" data-testid="support-workspace-topbar">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14">
          <div className="flex items-center gap-2">
            <button data-testid="support-workspace-mobile-menu-toggle" ref={triggerRef}
                    aria-label="Open support workspace navigation" aria-expanded={open}
                    aria-controls="support-workspace-mobile-navigation" onClick={() => setOpen(true)}
                    className="lg:hidden inline-flex items-center justify-center min-w-11 min-h-11 rounded-lg hover:bg-white/10">
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <Link to="/support-workspace/cases" className="flex items-center gap-2.5">
              <BrandLogo variant="icon" className="h-8 w-8 rounded-lg bg-white p-0.5" alt="ConfirmPH"
                          testId="support-workspace-brand-logo" />
              <div className="leading-none">
                <div className="font-display font-semibold text-sm">ConfirmPH</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 mt-1">Support Workspace</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3 text-xs text-emerald-100/90">
            <Link to="/" data-testid="support-workspace-view-site"
                   className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10">
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <span data-testid="support-workspace-current-user" className="hidden sm:inline truncate max-w-[200px]">
              {user?.email}
            </span>
            <button data-testid="support-workspace-logout-button" onClick={logout}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <aside className="fixed inset-y-14 left-0 w-52 bg-[#0F1F18] text-white p-3 hidden lg:flex flex-col z-30"
              data-testid="support-workspace-sidebar">
        <NavList onNavigate={() => setOpen(false)} />
      </aside>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" aria-hidden="true" data-overlay-backdrop
                onClick={() => setOpen(false)} data-testid="support-workspace-drawer-backdrop" />
          <aside className="fixed inset-y-0 left-0 w-72 bg-[#0F1F18] text-white p-4 z-50 lg:hidden overflow-y-auto"
                  ref={panelRef} id="support-workspace-mobile-navigation" role="dialog" aria-modal="true"
                  aria-label="Support workspace navigation" tabIndex={-1} data-testid="support-workspace-mobile-drawer">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-semibold">Support Workspace</span>
              <button data-testid="support-workspace-drawer-close" onClick={() => setOpen(false)}
                      aria-label="Close support workspace navigation"
                      className="min-w-11 min-h-11 inline-flex items-center justify-center rounded hover:bg-white/10">
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
            <NavList onNavigate={() => setOpen(false)} />
          </aside>
        </>
      )}

      <main className="lg:ml-52 px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-[1600px]" data-testid="support-workspace-main">
        <Outlet />
      </main>
    </div>
  );
}

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 space-y-0.5">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNavigate}
                  data-testid={`support-workspace-nav-${n.to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${
                      isActive ? "bg-emerald-600/20 text-emerald-100" : "text-slate-300 hover:bg-white/5"
                    }`}>
          <n.icon className="w-4 h-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}
